import {
  MATCH_COPY_CONTEXT_DEFAULTS,
  MATCH_COPY_SPORT_OPTIONS,
  MATCH_COPY_STYLE_OPTIONS,
  MATCH_COPY_VARIANT_OPTIONS,
  buildMatchCopyContext,
  buildMatchCopyLookupRoots,
  buildMatchCopyLookupPaths,
  getMatchCopyScopedPath,
  type MatchCopyContext,
  type MatchCopyScope,
  type MatchCopyStyle,
} from '../shared/matchCopyContext';

export type MatchCopyStepKey =
  | 'lobby'
  | 'game_on'
  | 'round_complete'
  | 'series_decision'
  | 'loser_challenge'
  | 'post_match'
  | 'global';

export type MatchCopyAudienceKey = 'host' | 'players' | 'shared' | 'actions' | 'sheets';
export type MatchCopyFieldKind = 'text' | 'rich_text';

export type MatchCopyField = {
  path: string;
  step: MatchCopyStepKey;
  audience: MatchCopyAudienceKey;
  label: string;
  help?: string;
  vars?: string[];
  kind?: MatchCopyFieldKind;
  placeholder?: string;
};

export type MatchCopyPreviewContext = MatchCopyContext;
export type MatchCopyEditorScope = MatchCopyScope;
export type { MatchCopyStyle };

export type MatchCopyHistoryItem = {
  id: string;
  settings_key: string;
  value: Record<string, unknown>;
  created_at: string;
  created_by: string | null;
  source: string;
  restored_from_id: string | null;
};

export const MATCH_COPY_DEFAULTS: Record<string, unknown> = {
  stepper: {
    heading: 'Match flow',
    labels: {
      lobby: 'Lobby',
      game_on: 'Play On',
      round_complete: 'Round Over',
      series_decision: 'What Next',
      loser_challenge: 'Bonus Puzzle',
      post_match: 'Results',
    },
  },
  sheets: {
    pointsInfo: {
      title: 'Points & leaderboard',
      bodyHtml:
        '<p><strong>The venue leaderboard is mostly wins.</strong></p>' +
        '<p>Your venue score is <strong>Wins × {winPoints}</strong> plus a small <strong>Upset Bonus</strong> when you beat a stronger opponent.</p>' +
        '<h3>What you earn</h3>' +
        '<ul>' +
        '<li><strong>Win:</strong> +{winPoints} points</li>' +
        '<li><strong>Upset Bonus:</strong> +0 to +5 points (only on wins)</li>' +
        '<li><strong>Loss / Tie:</strong> +0 points</li>' +
        '</ul>' +
        '<h3>Upset Bonus (0–5)</h3>' +
        '<p>Based on how much stronger the opponent is (using our internal skill rating). Bigger upsets earn a bigger bonus.</p>' +
        '<ul>' +
        '<li>Opponent is +0–99 rating: +0</li>' +
        '<li>Opponent is +100–199 rating: +1</li>' +
        '<li>Opponent is +200–299 rating: +2</li>' +
        '<li>Opponent is +300–399 rating: +3</li>' +
        '<li>Opponent is +400–499 rating: +4</li>' +
        '<li>Opponent is +500+ rating: +5</li>' +
        '</ul>' +
        '<h3>How ranking works</h3>' +
        '<p>We sort by venue score first. If two players have the same score, the upset bonus helps break ties.</p>' +
        '<hr />' +
        '<p><small>Tip: Venue points are separate for each sport and venue.</small></p>',
    },
  },
  content: {
    lobby: {
      participantsTitle: 'Participants',
      hostGuidance: {
        sessionLockedTeamsSet: "Teams are set. Start when everyone's ready.",
        sessionLocked: "Pick sides for rivals, then tap Start when everyone's ready.",
        default: "Invite players, then tap Start when everyone's here.",
      },
      participantGuidance: {
        joinSession: 'Tap Join Game below to play. The controller will start when ready.',
        join: 'Tap Join Game below to play! The host will add teammates or start when ready.',
        getNextSession: 'Tap Get Next below to be considered. The controller picks who plays next.',
        getNext: 'Tap Get Next below to join the challengers list. The host picks who plays next.',
        alreadyJoiningSession: 'Players are already joining. Choose a side to hop into the game.',
        alreadyJoining: 'The host already has a challenger. Choose a side to hop into the game.',
      },
    },
    loserChallenge: {
      modal: {
        title: 'Loser challenge',
        body: 'You lost this series. Choose to accept the loss or fire the bonus puzzle.',
      },
    },
  },
  controls: {
    stepTitles: {
      lobby: 'LOBBY',
      game_on: 'GAME ON',
      round_complete: 'GAME OVER',
      series_decision: "WHAT'S NEXT?",
      loser_challenge: 'BONUS PUZZLE',
      post_match: 'WRAP-UP',
    },
    resultButtons: {
      title: 'WHO WON THIS ROUND?',
      subtitle: 'Press and hold to confirm.',
      tieLabel: 'Tie',
      accessibilityHint: 'Press and hold to confirm and end the round.',
    },
    teamSelect: { title: 'PICK A TEAM', subtitle: 'Choose a side to continue.' },
    wrapCloseOnly: { title: 'WRAP-UP', subtitle: 'Game finished.', closeCta: 'CLOSE' },
    playerActions: {
      title: 'PLAYER ACTIONS',
      subtitleLobbyWait: 'Waiting for {controller} to set teams and start round.',
    },
    bonusDecision: {
      title: 'BONUS PUZZLE DECISION',
      subtitleHost: 'Accept defeat or trigger the bonus puzzle before time runs out.',
      subtitleWait: 'Waiting for the losing side to decide.',
      declineCta: 'TAKE THE L',
      acceptCta: 'CHALLENGE IT',
      closeCta: 'CLOSE',
    },
    roundCompleteGate: {
      title: 'GAME OVER',
      subtitleHost: 'Add a final score or tap NEXT to continue.',
      subtitleWait: 'Waiting for {host} to add a score or tap NEXT.',
      addScoreCta: 'ADD SCORE',
      nextCta: 'NEXT',
      closeCta: 'CLOSE',
    },
    whatsNextRoundCompleteHost: {
      title: "WHAT'S NEXT?",
      subtitle: 'Run it back or finish.',
    },
    recapFooter: {
      title: 'RECAP',
      subtitle: 'Finalize and share the game results!',
      addPhotosCta: 'ADD PHOTOS',
      addPhotosLoading: 'UPLOADING…',
      shareCta: 'SHARE RECAP',
    },
    optionsSheet: { title: 'Additional Options', closeCta: 'Close' },
    hostSection: {
      waitingTitle: "Who's playing?",
      waitingSubtitle: 'Invite players, then pick sides to start.',
      pickSidesSubtitle: 'Pick sides to start.',
      readySubtitle: 'Start game when ready!',
      scoreChallengeReadySubtitle: 'Start game when ready.',
    },
    entryCta: {
      joinTitle: 'JOIN',
      getNextTitle: 'GET NEXT',
      joinCta: 'Join game',
      joinHint: 'Join to pick a side.',
      leaveHint: 'Tap again to leave the list.',
      hostPicksHint: 'Host picks who plays next.',
    },
    leaveTeam: { label: 'Leave team' },
  },
  contexts: MATCH_COPY_CONTEXT_DEFAULTS.contexts,
  actions: {
    start_match: { label: 'Start', loadingLabel: 'Starting…' },
    cancel_match: { label: 'Cancel Game', loadingLabel: 'Cancelling…' },
    lock_teams: { label: 'Lock Teams', loadingLabel: 'Locking…' },
    abandon_match: { label: 'Cancel', loadingLabel: 'Cancelling…' },
    submit_result_a: { label: 'Win: Light', loadingLabel: 'Publishing…' },
    submit_result_b: { label: 'Win: Dark', loadingLabel: 'Publishing…' },
    submit_tie: { label: 'Declare Tie', loadingLabel: 'Publishing…' },
    end_practice: { label: 'Finish Challenge', loadingLabel: 'Ending…' },
    finish_match: { label: 'Finish', loadingLabel: 'Finishing…' },
    start_rematch: { label: 'Rematch', loadingLabel: 'Starting…' },
    join_match: { label: 'Join Game', loadingLabel: 'Joining…' },
    leave_match: { label: 'Leave Game', loadingLabel: 'Leaving…' },
    leave_team: { label: 'Leave Team', loadingLabel: 'Leaving…' },
    invite_players: { label: 'Invite Players', loadingLabel: 'Inviting…' },
    get_directions: { label: 'Directions' },
    show_qr: { label: 'QR Join' },
    join_team: { label: 'Join Team' },
    close: { label: 'Close' },
  },
};

export const MATCH_COPY_STEPS: Array<{ key: MatchCopyStepKey; label: string; number: number | null }> = [
  { key: 'lobby', label: 'Lobby', number: 1 },
  { key: 'game_on', label: 'Play On', number: 2 },
  { key: 'round_complete', label: 'Round Over', number: 3 },
  { key: 'series_decision', label: "What's Next", number: 4 },
  { key: 'loser_challenge', label: 'Bonus Puzzle', number: 5 },
  { key: 'post_match', label: 'Results', number: 6 },
  { key: 'global', label: 'Global', number: null },
];

export const MATCH_COPY_AUDIENCES: Array<{ key: MatchCopyAudienceKey; label: string }> = [
  { key: 'host', label: 'Host' },
  { key: 'players', label: 'Players' },
  { key: 'shared', label: 'Shared' },
  { key: 'actions', label: 'Actions' },
  { key: 'sheets', label: 'Sheets' },
];

const STEP_FIELDS: MatchCopyField[] = [
  { step: 'global', audience: 'shared', path: 'stepper.heading', label: 'Stepper heading', help: 'Heading shown above the stepper.' },
  { step: 'global', audience: 'sheets', path: 'sheets.pointsInfo.title', label: 'Points sheet title', help: 'Title shown at the top of Points & leaderboard sheet.' },
  { step: 'global', audience: 'sheets', path: 'sheets.pointsInfo.bodyHtml', label: 'Points sheet content', help: 'Rich text body for the points explainer. Supports {winPoints}.', vars: ['winPoints'], kind: 'rich_text' },

  { step: 'lobby', audience: 'host', path: 'content.lobby.hostGuidance.default', label: 'Controller guidance', help: 'Default guidance shown before teams are locked.' },
  { step: 'lobby', audience: 'host', path: 'content.lobby.hostGuidance.sessionLocked', label: 'Controller guidance (teams locked)' },
  { step: 'lobby', audience: 'host', path: 'content.lobby.hostGuidance.sessionLockedTeamsSet', label: 'Controller guidance (teams set)' },
  { step: 'lobby', audience: 'players', path: 'content.lobby.participantGuidance.join', label: 'Participant guidance (join)' },
  { step: 'lobby', audience: 'players', path: 'content.lobby.participantGuidance.joinSession', label: 'Participant guidance (join session)' },
  { step: 'lobby', audience: 'players', path: 'content.lobby.participantGuidance.getNext', label: 'Participant guidance (get next)' },
  { step: 'lobby', audience: 'players', path: 'content.lobby.participantGuidance.getNextSession', label: 'Participant guidance (get next session)' },
  { step: 'lobby', audience: 'players', path: 'content.lobby.participantGuidance.alreadyJoining', label: 'Participant guidance (already joining)' },
  { step: 'lobby', audience: 'players', path: 'content.lobby.participantGuidance.alreadyJoiningSession', label: 'Participant guidance (already joining session)' },
  { step: 'lobby', audience: 'players', path: 'content.lobby.participantsTitle', label: 'Participants title' },
  { step: 'lobby', audience: 'host', path: 'controls.hostSection.waitingTitle', label: 'Host section title' },
  { step: 'lobby', audience: 'host', path: 'controls.hostSection.waitingSubtitle', label: 'Host section subtitle (waiting)' },
  { step: 'lobby', audience: 'host', path: 'controls.hostSection.pickSidesSubtitle', label: 'Host section subtitle (pick sides)' },
  { step: 'lobby', audience: 'host', path: 'controls.hostSection.readySubtitle', label: 'Host section subtitle (ready)' },
  { step: 'lobby', audience: 'host', path: 'controls.hostSection.scoreChallengeReadySubtitle', label: 'Host section subtitle (score challenge ready)' },
  { step: 'lobby', audience: 'players', path: 'controls.entryCta.joinTitle', label: 'Entry CTA title (join)' },
  { step: 'lobby', audience: 'players', path: 'controls.entryCta.getNextTitle', label: 'Entry CTA title (get next)' },
  { step: 'lobby', audience: 'players', path: 'controls.entryCta.joinCta', label: 'Entry CTA button' },
  { step: 'lobby', audience: 'players', path: 'controls.entryCta.joinHint', label: 'Entry CTA hint (join)' },
  { step: 'lobby', audience: 'players', path: 'controls.entryCta.leaveHint', label: 'Entry CTA hint (leave)' },
  { step: 'lobby', audience: 'players', path: 'controls.entryCta.hostPicksHint', label: 'Entry CTA hint (host picks)' },
  { step: 'lobby', audience: 'players', path: 'controls.playerActions.subtitleLobbyWait', label: 'Player actions subtitle', help: 'Supports {controller}.', vars: ['controller'] },

  { step: 'game_on', audience: 'shared', path: 'stepper.labels.game_on', label: 'Stepper label' },
  { step: 'game_on', audience: 'shared', path: 'controls.stepTitles.game_on', label: 'Step title' },

  { step: 'round_complete', audience: 'shared', path: 'stepper.labels.round_complete', label: 'Stepper label' },
  { step: 'round_complete', audience: 'shared', path: 'controls.stepTitles.round_complete', label: 'Step title' },
  { step: 'round_complete', audience: 'host', path: 'controls.roundCompleteGate.title', label: 'Gate title' },
  { step: 'round_complete', audience: 'host', path: 'controls.roundCompleteGate.subtitleHost', label: 'Gate subtitle (host)' },
  { step: 'round_complete', audience: 'players', path: 'controls.roundCompleteGate.subtitleWait', label: 'Gate subtitle (players)', help: 'Supports {host}.', vars: ['host'] },
  { step: 'round_complete', audience: 'shared', path: 'controls.roundCompleteGate.addScoreCta', label: 'Gate CTA (add score)' },
  { step: 'round_complete', audience: 'shared', path: 'controls.roundCompleteGate.nextCta', label: 'Gate CTA (next)' },
  { step: 'round_complete', audience: 'shared', path: 'controls.roundCompleteGate.closeCta', label: 'Gate CTA (close)' },
  { step: 'round_complete', audience: 'host', path: 'controls.resultButtons.title', label: 'Result buttons title' },
  { step: 'round_complete', audience: 'host', path: 'controls.resultButtons.subtitle', label: 'Result buttons subtitle' },
  { step: 'round_complete', audience: 'host', path: 'controls.resultButtons.tieLabel', label: 'Tie label' },
  { step: 'round_complete', audience: 'host', path: 'controls.resultButtons.accessibilityHint', label: 'Accessibility hint' },

  { step: 'series_decision', audience: 'shared', path: 'stepper.labels.series_decision', label: 'Stepper label' },
  { step: 'series_decision', audience: 'shared', path: 'controls.stepTitles.series_decision', label: 'Step title' },
  { step: 'series_decision', audience: 'host', path: 'controls.whatsNextRoundCompleteHost.title', label: "What's next title" },
  { step: 'series_decision', audience: 'host', path: 'controls.whatsNextRoundCompleteHost.subtitle', label: "What's next subtitle" },

  { step: 'loser_challenge', audience: 'shared', path: 'stepper.labels.loser_challenge', label: 'Stepper label' },
  { step: 'loser_challenge', audience: 'shared', path: 'controls.stepTitles.loser_challenge', label: 'Step title' },
  { step: 'loser_challenge', audience: 'shared', path: 'controls.bonusDecision.title', label: 'Bonus decision title' },
  { step: 'loser_challenge', audience: 'host', path: 'controls.bonusDecision.subtitleHost', label: 'Bonus decision subtitle (host)' },
  { step: 'loser_challenge', audience: 'players', path: 'controls.bonusDecision.subtitleWait', label: 'Bonus decision subtitle (players)' },
  { step: 'loser_challenge', audience: 'shared', path: 'controls.bonusDecision.declineCta', label: 'Bonus decision CTA (decline)' },
  { step: 'loser_challenge', audience: 'shared', path: 'controls.bonusDecision.acceptCta', label: 'Bonus decision CTA (accept)' },
  { step: 'loser_challenge', audience: 'shared', path: 'controls.bonusDecision.closeCta', label: 'Bonus decision CTA (close)' },
  { step: 'loser_challenge', audience: 'host', path: 'content.loserChallenge.modal.title', label: 'Loser challenge modal title' },
  { step: 'loser_challenge', audience: 'host', path: 'content.loserChallenge.modal.body', label: 'Loser challenge modal body' },

  { step: 'post_match', audience: 'shared', path: 'stepper.labels.post_match', label: 'Stepper label' },
  { step: 'post_match', audience: 'shared', path: 'controls.stepTitles.post_match', label: 'Step title' },
  { step: 'post_match', audience: 'shared', path: 'controls.recapFooter.title', label: 'Recap footer title' },
  { step: 'post_match', audience: 'shared', path: 'controls.recapFooter.subtitle', label: 'Recap footer subtitle' },
  { step: 'post_match', audience: 'shared', path: 'controls.recapFooter.addPhotosCta', label: 'Add photos CTA' },
  { step: 'post_match', audience: 'shared', path: 'controls.recapFooter.addPhotosLoading', label: 'Add photos loading CTA' },
  { step: 'post_match', audience: 'shared', path: 'controls.recapFooter.shareCta', label: 'Share CTA' },
  { step: 'post_match', audience: 'players', path: 'controls.wrapCloseOnly.title', label: 'Wrap close-only title' },
  { step: 'post_match', audience: 'players', path: 'controls.wrapCloseOnly.subtitle', label: 'Wrap close-only subtitle' },
  { step: 'post_match', audience: 'players', path: 'controls.wrapCloseOnly.closeCta', label: 'Wrap close-only CTA' },

  { step: 'global', audience: 'shared', path: 'controls.teamSelect.title', label: 'Team select title' },
  { step: 'global', audience: 'shared', path: 'controls.teamSelect.subtitle', label: 'Team select subtitle' },
  { step: 'global', audience: 'shared', path: 'controls.optionsSheet.title', label: 'Options sheet title' },
  { step: 'global', audience: 'shared', path: 'controls.optionsSheet.closeCta', label: 'Options sheet close CTA' },
  { step: 'global', audience: 'shared', path: 'controls.playerActions.title', label: 'Player actions title' },
  { step: 'global', audience: 'shared', path: 'controls.leaveTeam.label', label: 'Leave team label' },
];

const ACTION_FIELDS: MatchCopyField[] = [
  ['start_match', 'Start match'],
  ['lock_teams', 'Lock teams'],
  ['invite_players', 'Invite players'],
  ['join_match', 'Join match'],
  ['leave_match', 'Leave match'],
  ['leave_team', 'Leave team'],
  ['cancel_match', 'Cancel match'],
  ['abandon_match', 'Abandon match'],
  ['submit_result_a', 'Submit result (light)'],
  ['submit_result_b', 'Submit result (dark)'],
  ['submit_tie', 'Submit tie'],
  ['finish_match', 'Finish'],
  ['start_rematch', 'Rematch'],
  ['end_practice', 'Finish challenge'],
  ['get_directions', 'Directions'],
  ['show_qr', 'QR Join'],
  ['join_team', 'Join team'],
  ['close', 'Close'],
].flatMap(
  ([key, label]): MatchCopyField[] => [
    { step: 'global', audience: 'actions', path: `actions.${key}.label`, label: `${label} label` },
    { step: 'global', audience: 'actions', path: `actions.${key}.loadingLabel`, label: `${label} loading` },
  ],
);

export const MATCH_COPY_FIELDS: MatchCopyField[] = [...STEP_FIELDS, ...ACTION_FIELDS];

export const PREVIEW_SAMPLE_VARS = {
  host: 'Rudy',
  controller: 'Sabi67',
  winPoints: 10,
  copyStyle: 'game',
  sportId: 'pickleball',
  variant: 'high_score',
  activityLabel: 'Bench Press',
};

export const MATCH_COPY_STYLE_EDITOR_OPTIONS = MATCH_COPY_STYLE_OPTIONS;
export const MATCH_COPY_SPORT_EDITOR_OPTIONS = MATCH_COPY_SPORT_OPTIONS;
export const MATCH_COPY_VARIANT_EDITOR_OPTIONS = MATCH_COPY_VARIANT_OPTIONS;

export function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function deepMerge(base: unknown, override: unknown): unknown {
  if (!isObject(base) || !isObject(override)) return override ?? base;
  const out: Record<string, unknown> = { ...base };
  Object.entries(override).forEach(([key, value]) => {
    out[key] = deepMerge(base[key], value);
  });
  return out;
}

export function deepDiff(base: unknown, value: unknown): unknown | undefined {
  if (value === undefined) return undefined;
  if (!isObject(base) || !isObject(value)) return value === base ? undefined : value;
  const out: Record<string, unknown> = {};
  const keys = new Set([...Object.keys(base), ...Object.keys(value)]);
  keys.forEach((key) => {
    const next = deepDiff(base[key], value[key]);
    if (next !== undefined) out[key] = next as unknown;
  });
  return Object.keys(out).length ? out : undefined;
}

export function stableStringify(value: unknown) {
  const seen = new WeakSet();
  const sortKeys = (input: unknown): unknown => {
    if (Array.isArray(input)) return input.map(sortKeys);
    if (!isObject(input)) return input;
    if (seen.has(input as object)) return input;
    seen.add(input as object);
    const out: Record<string, unknown> = {};
    Object.keys(input)
      .sort()
      .forEach((key) => {
        out[key] = sortKeys(input[key]);
      });
    return out;
  };
  return JSON.stringify(sortKeys(value), null, 2);
}

export function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function getPathValue(source: unknown, path: string): unknown {
  const parts = path.split('.').filter(Boolean);
  let current: unknown = source;
  for (const part of parts) {
    if (!isObject(current)) return undefined;
    current = current[part];
  }
  return current;
}

export function getContextualPathValue(
  source: unknown,
  path: string,
  context?: Partial<MatchCopyPreviewContext> | null
): unknown {
  const candidates = buildMatchCopyLookupPaths(path, context);
  for (const candidate of candidates) {
    const value = getPathValue(source, candidate);
    if (value !== undefined) return value;
  }
  return undefined;
}

export function setPathValue(target: Record<string, unknown>, path: string, value: string) {
  const parts = path.split('.').filter(Boolean);
  if (!parts.length) return;
  let current: Record<string, unknown> = target;
  for (let index = 0; index < parts.length - 1; index += 1) {
    const part = parts[index];
    if (!isObject(current[part])) current[part] = {};
    current = current[part] as Record<string, unknown>;
  }
  current[parts[parts.length - 1]] = value;
}

export function deletePathValue(target: Record<string, unknown>, path: string) {
  const parts = path.split('.').filter(Boolean);
  if (!parts.length) return;
  let current: Record<string, unknown> = target;
  for (let index = 0; index < parts.length - 1; index += 1) {
    const part = parts[index];
    if (!isObject(current[part])) return;
    current = current[part] as Record<string, unknown>;
  }
  delete current[parts[parts.length - 1]];
}

export function normalizeMatchCopyOverrides(value: Record<string, unknown>) {
  const merged = deepMerge(MATCH_COPY_DEFAULTS, value);
  return (deepDiff(MATCH_COPY_DEFAULTS, merged) as Record<string, unknown> | undefined) ?? {};
}

export function getEffectiveMatchCopy(
  overrides: Record<string, unknown>,
  context?: Partial<MatchCopyPreviewContext> | null
) {
  const effective = deepMerge(MATCH_COPY_DEFAULTS, overrides) as Record<string, unknown>;
  if (!context) return effective;
  const resolvedContext = buildMatchCopyContext(context);
  let merged = effective;
  [...buildMatchCopyLookupRoots(resolvedContext)].reverse().forEach((candidate) => {
    const scopedValue = getPathValue(effective, candidate);
    if (isObject(scopedValue)) merged = deepMerge(merged, scopedValue) as Record<string, unknown>;
  });
  return merged;
}

export function formatTemplate(
  template: string,
  vars: Record<string, string | number | null | undefined> = PREVIEW_SAMPLE_VARS,
) {
  return template.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, key) => {
    const value = vars[key];
    if (value === null || value === undefined) return match;
    return String(value);
  });
}

export function textValue(value: unknown) {
  return typeof value === 'string' ? value : '';
}

export function shortUserLabel(value: string | null | undefined) {
  if (!value) return 'Unknown admin';
  return value.length <= 12 ? value : `${value.slice(0, 8)}…`;
}

export function fieldsForStep(step: MatchCopyStepKey) {
  return MATCH_COPY_FIELDS.filter((field) => field.step === step);
}

export function fieldsForAudience(step: MatchCopyStepKey, audience: MatchCopyAudienceKey) {
  return MATCH_COPY_FIELDS.filter((field) => field.step === step && field.audience === audience);
}

export function countOverridesForFields(
  overrides: Record<string, unknown>,
  fields: MatchCopyField[],
  scope: MatchCopyEditorScope = { kind: 'base' }
) {
  return fields.filter((field) => textValue(getPathValue(overrides, getMatchCopyScopedPath(field.path, scope))).trim().length > 0).length;
}

export function resolveScopedFieldPath(path: string, scope: MatchCopyEditorScope) {
  return getMatchCopyScopedPath(path, scope);
}
